import createClient, { type Middleware } from 'openapi-fetch';

import { getConfig } from '@/shared/config';

import {
  getAuthEndpointChecker,
  getForbiddenHandler,
  getLogoutHandler,
  getRefreshHandler,
  getTokenProvider,
} from './di';
import { normalizeError } from './normalize-error';

import type { paths } from './__generated__/schema';

/**
 * Cliente HTTP tipado del frontend ANI (ADR-005).
 *
 * Backed by openapi-fetch sobre `fetch` nativo. Hay una sola instancia
 * para toda la app — el middleware encadena:
 *
 *  1. Inyeccion de `Authorization: Bearer <token>` cuando hay token.
 *  2. Stashing del Request clonado (para poder reintentar despues de
 *     refresh sin perder el body, que es un stream).
 *  3. Refresh transparente en 401 (una sola vez por request, no para
 *     endpoints de `/auth/*`).
 *  4. Logout local si el refresh falla.
 *  5. Desempaque del envelope `{ success, data, timestamp }` que el
 *     backend NestJS aplica via `TransformInterceptor` global.
 *
 * El error final lo normaliza `apiCall()` a `ApiError` — los componentes
 * jamas ven un body crudo de fetch o de openapi-fetch.
 */

const requestStash = new WeakMap<Request, Request>();

/**
 * Single-flight refresh: si llegan N requests con 401 simultaneos, solo UNA
 * dispara `refreshHandler()`; las demas esperan el mismo resultado. Sin esto,
 * el backend ve N usos del mismo refresh token, marca replay y revoca toda
 * la `familyId` -> el usuario pierde la sesion (ver doc 03-refresh.md §7.5).
 */
let inflightRefresh: Promise<void> | null = null;

function refreshOnce(): Promise<void> {
  inflightRefresh ??= getRefreshHandler()().finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}

async function unwrapEnvelopeFromResponse(response: Response): Promise<Response> {
  if (!response.ok || !(response.headers.get('content-type') ?? '').includes('application/json')) {
    return response;
  }
  const body = (await response.clone().json()) as unknown;
  if (
    body !== null &&
    typeof body === 'object' &&
    'success' in body &&
    'data' in body &&
    (body as { success: unknown }).success === true
  ) {
    const inner = (body as { data: unknown }).data;
    return new Response(JSON.stringify(inner), {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    });
  }
  return response;
}

const middleware: Middleware = {
  onRequest({ request }) {
    requestStash.set(request, request.clone());
    const token = getTokenProvider()();
    if (token) request.headers.set('Authorization', `Bearer ${token}`);
    return request;
  },

  async onResponse({ request, response }) {
    // 403 = el server rechazó por permisos/jerarquía. La sesión sigue
    // viva (no es 401), pero el snapshot local de `SessionUser` puede
    // estar desactualizado (ej. otro admin nos bajó el rol en vivo).
    // Disparamos rehidratación silenciosa para resincronizar permisos
    // y que la UI vuelva a ocultar lo que ya no corresponde.
    if (response.status === 403 && !getAuthEndpointChecker()(request.url)) {
      try {
        getForbiddenHandler()();
      } catch {
        // El handler debe ser fire-and-forget; nunca debe romper el flujo.
      }
    }

    if (
      response.status === 401 &&
      !getAuthEndpointChecker()(request.url) &&
      request.headers.get('x-ani-retried') !== '1'
    ) {
      const stashed = requestStash.get(request);
      if (stashed) {
        try {
          await refreshOnce();
        } catch (refreshError) {
          // TOKEN_REUSE_DETECTED es indicio de robo de cookie o bug de
          // single-flight: el backend revoco toda la familia. Logueamos
          // con severidad alta para alertar (doc 03-refresh.md §7.5 #3).
          if (
            refreshError !== null &&
            typeof refreshError === 'object' &&
            'code' in refreshError &&
            refreshError.code === 'TOKEN_REUSE_DETECTED'
          ) {
            console.warn('[security] TOKEN_REUSE_DETECTED — sesion cerrada por seguridad.');
          }
          getLogoutHandler()();
          return response;
        }
        const newToken = getTokenProvider()();
        const retryHeaders = new Headers(stashed.headers);
        retryHeaders.set('x-ani-retried', '1');
        if (newToken) retryHeaders.set('Authorization', `Bearer ${newToken}`);
        const retried = await globalThis.fetch(stashed.url, {
          method: stashed.method,
          headers: retryHeaders,
          body: stashed.body,
          credentials: stashed.credentials,
        });
        // El retry no pasa por el middleware de envelope-unwrap, asi que lo
        // aplicamos aqui manualmente para mantener la simetria.
        return unwrapEnvelopeFromResponse(retried);
      }
    }

    return unwrapEnvelopeFromResponse(response);
  },
};

export const apiClient = createClient<paths>({
  baseUrl: getConfig().apiBaseUrl,
  credentials: 'include',
  // Fetch perezoso: openapi-fetch captura la referencia al crear el cliente.
  // Si MSW (o cualquier interceptor) patchea `globalThis.fetch` despues, los
  // tests no interceptarian. Esta funcion garantiza que cada llamada lee la
  // version actual de la global.
  fetch: (request) => globalThis.fetch(request),
});

apiClient.use(middleware);

/**
 * Shape que openapi-fetch produce para CUALQUIER llamada `.GET/.POST/...`:
 * union discriminada entre exito (con `data`) y error (con `error`).
 * El `T` final lo provee el caller en el endpoint (`apiCall<LoginResponse>(...)`).
 */
type FetchResult = { data?: unknown; error?: unknown; response: Response };

/**
 * Adapta el shape `{ data, error, response }` de openapi-fetch al
 * contrato historico del frontend: resuelve con la data o rechaza con
 * `ApiError`. Lo usa cada endpoint para evitar duplicar el if/throw.
 */
export async function apiCall<T>(promise: Promise<FetchResult>): Promise<T> {
  let result: FetchResult;
  try {
    result = await promise;
  } catch (networkError) {
    // openapi-fetch rechaza si el fetch falla (red caida, timeout, abort).
    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
    return Promise.reject(normalizeError(networkError));
  }

  const { data, error, response } = result;
  if (error !== undefined || !response.ok) {
    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
    return Promise.reject(normalizeError(error, response));
  }
  return data as T;
}
